import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {PreparedBlock} from '@github-ui/taillights-worker/document'
import {startTransition, useEffect, useReducer, type RefObject} from 'react'
import {useTaillightContext} from './taillight-context'
import {bindTaillightText} from './taillight-dom'
import type {TaillightProps} from './taillight-types'
import {useTaillightsController, useTaillightsCSSScope} from './taillights-provider'

/** Binds prepared syntax ranges to native text and reports binding failures without replacing that text. */
function CSSPaint({block, paintRef}: {block: PreparedBlock; paintRef: RefObject<HTMLElement | null>}) {
  const [error, fail] = useReducer((_previous: Error | null, next: Error) => next, null)
  const scope = useTaillightsCSSScope()
  useEffect(() => {
    function reportFailure(failure: unknown) {
      const nextError = failure instanceof Error ? failure : new Error(String(failure))
      startTransition(() => fail(nextError))
    }
    try {
      return scope.register(bindTaillightText(paintRef.current, block.text), block, block.palette.colors)
    } catch (failure) {
      reportFailure(failure)
    }
  }, [block, paintRef, scope])
  if (error) throw error
  return null
}

/** Highlights native text with CSS ranges, preserving its DOM and controls without requesting bitmap paint. */
export function TaillightCSS(props: TaillightProps) {
  const {block} = props
  const controller = useTaillightsController()
  const {nativeSourceRef} = useTaillightContext()
  return controller && block.paint !== 'none' ? (
    <ErrorBoundary key={props.block.imageKey} boundaryName="TaillightCSS" fallback={null} onError={props.onRenderError}>
      <CSSPaint block={block} paintRef={nativeSourceRef} />
    </ErrorBoundary>
  ) : null
}
